import { useEffect, useRef } from "react";
import "altcha";
// The `altcha` package's default export ("./") only ships the generic
// custom-element typings (HTMLElementTagNameMap). Its React JSX augmentation
// for `<altcha-widget>` lives behind this subpath — pulling it in here means
// we don't have to hand-roll a `declare module "react"` augmentation.
import "altcha/types/react";
// Registers Dutch strings on the shared `$altcha.i18n` store as a side
// effect (`globalThis.$altcha.i18n.set("nl", ...)`) — the page is fully
// Dutch, and the widget defaults to English otherwise.
import "altcha/i18n/nl";

type AltchaState = { state?: string; payload?: string };

/**
 * Wraps the `altcha-widget` custom element: it self-solves the proof-of-work
 * challenge on load and reports the resulting payload (or its absence, on
 * error/expiry) via `onPayload`.
 *
 * Note: the widget's own `challenge` attribute doubles as "URL to fetch a
 * challenge from" whenever the value isn't JSON — that's what `challengeUrl`
 * is wired to below.
 */
export function AltchaWidget({
  challengeUrl,
  onPayload,
}: {
  challengeUrl: string;
  onPayload: (payload: string | null) => void;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onState = (ev: Event) => {
      const detail = (ev as CustomEvent<AltchaState>).detail;
      if (detail?.state === "verified" && detail.payload) {
        onPayload(detail.payload);
      } else if (detail?.state === "error" || detail?.state === "expired") {
        onPayload(null);
      }
    };
    el.addEventListener("statechange", onState);
    return () => el.removeEventListener("statechange", onState);
  }, [onPayload]);

  return (
    <altcha-widget
      ref={ref}
      challenge={challengeUrl}
      auto="onload"
      language="nl"
      // ALTCHA positions its error popover below the 70px widget; reserve that
      // space so the popover never covers the following submit button.
      style={{
        "--altcha-max-width": "100%",
        display: "block",
        width: "100%",
        paddingBottom: "1.5rem",
      }}
    />
  );
}
